import { useEffect, useRef, useState } from 'react';
import { ModelCapabilityPills } from './ModelCatalogCells';
import { Button } from '@localagent/ui/components/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@localagent/ui/components/dialog';
import { useDiscoverProvider } from '../../hooks/useProviderManagement';
import type { DiscoveredModelView, ProviderType } from '@localagent/sdk';

export interface ProviderDiscoverDialogProps {
  provider: ProviderType;
  open: boolean;
  onClose: () => void;
  /** Resolves the desktop device id — required only for lmstudio (bridge). */
  getDeviceId?: () => Promise<string | null>;
  /** Called after a successful import with the count persisted. */
  onImported?: (imported: number) => void;
}

type Phase = 'loading' | 'ready' | 'importing';

/**
 * Provider discovery checklist. Opens by PREVIEWing the
 * provider's live model list (fetch-only, persists nothing), renders the
 * candidates as a checklist, and IMPORTs only the checked ones as managed
 * account entries. Cloud providers fetch server-side (web + desktop); LM Studio
 * routes through the desktop bridge via `getDeviceId`.
 */
export function ProviderDiscoverDialog({ provider, open, onClose, getDeviceId, onImported }: ProviderDiscoverDialogProps) {
  const discover = useDiscoverProvider();
  const [candidates, setCandidates] = useState<DiscoveredModelView[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>('loading');

  // The parent may pass getDeviceId as an inline closure; read it through a
  // ref so the open-preview effect keys only on (open, provider).
  const getDeviceIdRef = useRef(getDeviceId);
  getDeviceIdRef.current = getDeviceId;
  const { mutate: discoverMutate } = discover; // stable across renders (react-query)

  // Preview when the dialog opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(null);
    setCandidates([]);
    setSelected(new Set());
    setPhase('loading');
    void (async () => {
      let deviceId: string | undefined;
      if (provider === 'lmstudio') {
        const getId = getDeviceIdRef.current;
        const id = getId ? await getId() : null;
        if (!id) {
          if (!cancelled) { setError('LM Studio discovery needs the desktop app running and connected.'); setPhase('ready'); }
          return;
        }
        deviceId = id;
      }
      discoverMutate(
        { provider, deviceId },
        {
          onSuccess: (result) => {
            if (cancelled) return;
            setCandidates(result.candidates);
            // Pre-select the candidates the account does not already have.
            setSelected(
              new Set(
                result.candidates
                  // Never pre-select a candidate with no param spec: importing it
                  // is refused 422, so pre-checking it queues a guaranteed error
                  // the user did not ask for.
                  .filter(c => !c.already_imported && c.importable !== false)
                  .map(c => c.name),
              ),
            );
            setPhase('ready');
          },
          onError: (e) => { if (!cancelled) { setError(e.message); setPhase('ready'); } },
        },
      );
    })();
    return () => { cancelled = true; };
  }, [open, provider, discoverMutate]);

  const toggle = (name: string) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });

  const handleImport = async () => {
    if (selected.size === 0) return;
    setPhase('importing');
    setError(null);
    let deviceId: string | undefined;
    if (provider === 'lmstudio') {
      deviceId = (getDeviceId ? await getDeviceId() : null) ?? undefined;
    }
    discover.mutate(
      { provider, deviceId, modelIds: Array.from(selected) },
      {
        onSuccess: (result) => { onImported?.(result.imported); onClose(); },
        onError: (e) => { setError(e.message); setPhase('ready'); },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="provider-discover-dialog">
        <DialogHeader>
          <DialogTitle>Discover models from {provider}</DialogTitle>
          <DialogDescription>
            Select the models to import as managed account entries. Models with no reported
            context window (—) import as incomplete and must be filled before an agent can use them.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <p className="text-sm text-destructive" data-testid="provider-discover-error">{error}</p>
        )}
        {phase === 'loading' && (
          <p className="text-sm text-muted-foreground">Fetching models…</p>
        )}
        {phase !== 'loading' && candidates.length === 0 && !error && (
          <p className="text-sm text-muted-foreground" data-testid="provider-discover-empty">No models returned.</p>
        )}

        {candidates.length > 0 && (
          <div className="max-h-96 overflow-y-auto" data-testid="provider-discover-list">
            {candidates.map(c => (
              <label
                key={c.name}
                className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 hover:bg-accent/50"
                data-testid={`discover-candidate-${c.name}`}
              >
                <input
                  type="checkbox"
                  checked={selected.has(c.name)}
                  onChange={() => toggle(c.name)}
                  disabled={c.importable === false}
                  data-testid={`discover-check-${c.name}`}
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{c.display_name || c.name}</div>
                  <div className="truncate font-mono text-xs text-muted-foreground">{c.name}</div>
                  {/*
                    The provider reported no metadata for this model, so there is no
                    param spec to store. Importing it would persist
                    supported_params = NULL, which the fail-closed validator turns
                    into EVERY param rejected — the model would look imported and be
                    unusable, with nothing on screen to say why.
                  */}
                  {c.spec_unavailable_reason && (
                    <div
                      className="mt-0.5 text-[11px] text-amber-600 dark:text-amber-500"
                      data-testid={`discover-unavailable-${c.name}`}
                    >
                      {c.spec_unavailable_reason}
                    </div>
                  )}
                </div>
                {/*
                  Shared with the models table. The previous inline
                  `c.supports_tools && …` rendered a KNOWN FALSE and a SILENT
                  provider identically — both as nothing — which is exactly the
                  tri-state conflation the catalog cells forbid.
                */}
                <ModelCapabilityPills flags={c} />
                <div className="whitespace-nowrap text-right text-xs text-muted-foreground">
                  {c.context_window != null ? `${c.context_window.toLocaleString()} ctx` : '— ctx'}
                </div>
                {c.already_imported && (
                  <span className="whitespace-nowrap text-[11px] text-muted-foreground" data-testid={`discover-imported-${c.name}`}>imported</span>
                )}
              </label>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            data-testid="provider-discover-import"
            disabled={selected.size === 0 || phase !== 'ready'}
            onClick={() => { void handleImport(); }}
          >
            {phase === 'importing' ? 'Importing…' : `Import ${selected.size} selected`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
